import { storage, auth } from '../src/firebaseConfig';
import { resolveProxyStorageUrl } from '../src/utils/apiBase';
import { ref, uploadString, getBytes, deleteObject } from 'firebase/storage';
import { Chapter } from '../src/types';
import { resolveChapterProseText } from '../src/utils/chapterProseResolver';

export interface StorageChapter {
  chapterNumber: number;
  title: string;
  summary?: string;
  draftText?: string;
  finalText: string;
  chapterContract?: any;
  inspectionReport?: any;
  editorReport?: any;
  archivistNotes?: any;
  createdAt: string;
  updatedAt: string;
  editedText?: string;
  editedByUser?: boolean;
  lastEditedAt?: string;
  content?: string;
  text?: string;
  chapterText?: string;
  recoverySummary?: string;
  recoveryMetadata?: unknown;
}

export interface StorageLoadError {
  code: 'UNAUTHENTICATED' | 'FORBIDDEN' | 'NOT_FOUND' | 'INVALID_PAYLOAD' | 'NETWORK' | 'UNKNOWN';
  message: string;
  technicalDetail?: string;
}

export interface StoredChapterPayload {
  chapterNumber: number;
  title: string;
  content: string;
  summary: string;
  recoveryMetadata?: unknown;
}

let lastStorageLoadError: StorageLoadError | null = null;

export function getLastStorageLoadError(): StorageLoadError | null {
  return lastStorageLoadError;
}

function setLastStorageLoadError(error: StorageLoadError | null): void {
  lastStorageLoadError = error;
}

function isLikelyNetworkOrCorsFailure(error: unknown): boolean {
  const msg = error instanceof Error ? error.message.toLowerCase() : String(error || '').toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network request failed');
}

function normalizeStorageChapterPayload(raw: Partial<StorageChapter> | null): StorageChapter | null {
  if (!raw || typeof raw !== 'object') return null;
  const resolvedProse = resolveChapterProseText(raw as Record<string, unknown>);
  if (!resolvedProse) return null;
  return {
    chapterNumber: Number(raw.chapterNumber ?? 0),
    title: typeof raw.title === 'string' && raw.title.trim() ? raw.title : 'Recovered Chapter',
    summary: typeof raw.summary === 'string' ? raw.summary : undefined,
    finalText: resolvedProse,
    draftText: raw.draftText,
    chapterContract: raw.chapterContract,
    inspectionReport: raw.inspectionReport,
    editorReport: raw.editorReport,
    archivistNotes: raw.archivistNotes,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
    editedText: raw.editedText,
    editedByUser: raw.editedByUser,
    lastEditedAt: raw.lastEditedAt,
    content: raw.content,
    text: raw.text,
    chapterText: raw.chapterText,
    recoverySummary: raw.recoverySummary,
    recoveryMetadata: raw.recoveryMetadata,
  };
}

async function loadChapterViaProxy(storagePath: string, token: string): Promise<StorageChapter | null> {
  const proxyUrl = resolveProxyStorageUrl(storagePath);
  const response = await fetch(proxyUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 404) {
    throw { code: 'NOT_FOUND', message: 'Storage object was not found' };
  }
  if (response.status === 403) {
    throw { code: 'FORBIDDEN', message: 'Forbidden path for storage proxy' };
  }
  if (response.status === 401) {
    throw { code: 'UNAUTHENTICATED', message: 'Storage proxy authentication failed' };
  }
  if (!response.ok) {
    throw { code: 'NETWORK', message: `Storage proxy failed (${response.status})` };
  }
  const parsed = (await response.json()) as Partial<StorageChapter>;
  const normalized = normalizeStorageChapterPayload(parsed);
  if (!normalized) {
    throw { code: 'INVALID_PAYLOAD', message: 'Storage JSON missing chapter prose' };
  }
  return normalized;
}

async function loadChapterViaSdk(storagePath: string): Promise<StorageChapter | null> {
  const storageRef = ref(storage, storagePath);
  const bytes = await getBytes(storageRef);
  const decodedText = new TextDecoder('utf-8').decode(bytes);
  const parsed = JSON.parse(decodedText) as Partial<StorageChapter>;
  return normalizeStorageChapterPayload(parsed);
}

export const buildChapterStoragePath = (uid: string, projectId: string, chapterNumber: number): string => {
  return `users/${uid}/projects/${projectId}/chapters/chapter-${chapterNumber}.json`;
};

export const saveChapterContentToStorage = async (
  uid: string,
  projectId: string,
  chapterData: StorageChapter
): Promise<string> => {
  const startTime = Date.now();
  const path = buildChapterStoragePath(uid, projectId, chapterData.chapterNumber);
  const storageRef = ref(storage, path);
  
  const contentToUpload = JSON.stringify(chapterData);
  const payloadSize = contentToUpload.length;

  await uploadString(storageRef, contentToUpload, 'raw', {
    contentType: 'application/json'
  });
  
  const duration = Date.now() - startTime;
  console.log(`[STORAGE AUDIT] Save to Firebase Storage | Duration: ${duration}ms | Payload Size: ${payloadSize} chars`);

  return path;
};

export const saveEditedChapterToStorage = async (
  uid: string,
  projectId: string,
  chapterNumber: number,
  editedText: string
): Promise<string> => {
  const path = buildChapterStoragePath(uid, projectId, chapterNumber);
  const existingChapter = await loadChapterContentFromStorage(path);
  
  if (!existingChapter) {
    throw new Error(`Cannot save edits: Chapter ${chapterNumber} does not exist in storage.`);
  }
  
  existingChapter.editedText = editedText;
  existingChapter.editedByUser = true;
  existingChapter.lastEditedAt = new Date().toISOString();
  
  return await saveChapterContentToStorage(uid, projectId, existingChapter);
};

export const loadChapterContentFromStorage = async (
  storagePath: string
): Promise<StorageChapter | null> => {
  try {
    setLastStorageLoadError(null);
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");
    const token = await user.getIdToken();
    try {
      return await loadChapterViaProxy(storagePath, token);
    } catch (proxyErr: any) {
      if (proxyErr?.code === 'NOT_FOUND') {
        setLastStorageLoadError({ code: 'NOT_FOUND', message: 'Storage object was not found' });
        return null;
      }
      if (proxyErr?.code === 'FORBIDDEN' || proxyErr?.code === 'UNAUTHENTICATED' || proxyErr?.code === 'INVALID_PAYLOAD') {
        setLastStorageLoadError({
          code: proxyErr.code,
          message: proxyErr.message || 'Storage proxy failed',
          technicalDetail: 'Storage read blocked; backend fallback failed.',
        });
        return null;
      }
      if (isLikelyNetworkOrCorsFailure(proxyErr) || proxyErr?.code === 'NETWORK') {
        try {
          return await loadChapterViaSdk(storagePath);
        } catch (sdkErr: any) {
          setLastStorageLoadError({
            code: sdkErr?.code === 'storage/object-not-found' ? 'NOT_FOUND' : 'NETWORK',
            message:
              sdkErr?.code === 'storage/object-not-found'
                ? 'Storage object was not found'
                : 'Storage read blocked; backend fallback failed.',
            technicalDetail: sdkErr?.message || 'Unknown SDK storage failure',
          });
          return null;
        }
      }
      throw proxyErr;
    }
  } catch (error: any) {
    if (error?.code === 'storage/object-not-found') {
      setLastStorageLoadError({ code: 'NOT_FOUND', message: 'Storage object was not found' });
      console.log(`[STORAGE] Object not found at ${storagePath}`);
    } else if (String(error?.message || '').includes('Not authenticated')) {
      setLastStorageLoadError({ code: 'UNAUTHENTICATED', message: 'Not authenticated to load chapter' });
    } else {
      setLastStorageLoadError({
        code: 'UNKNOWN',
        message: 'Storage hydration failed',
        technicalDetail: error?.message || 'Unknown error',
      });
      console.error("Error loading chapter from storage:", error);
    }
    return null;
  }
};

export const loadChapterFromStorage = async (storagePath: string): Promise<StoredChapterPayload> => {
  const data = await loadChapterContentFromStorage(storagePath);
  if (!data) {
    const failure = getLastStorageLoadError();
    throw new Error(failure?.message || 'This chapter exists, but its manuscript file could not be loaded.');
  }
  return {
    chapterNumber: data.chapterNumber,
    title: data.title,
    content: resolveChapterProseText(data as Record<string, unknown>),
    summary: data.summary || '',
    recoveryMetadata: data.recoveryMetadata,
  };
};

export const migrateFirestoreChapterToStorage = async (
  uid: string,
  projectId: string,
  chapter: Chapter
): Promise<string | null> => {
  if (chapter.storagePath) {
    return chapter.storagePath; // Already migrated
  }
  
  if (!chapter.content) {
    return null; // Nothing to migrate
  }

  const storageData: StorageChapter = {
    chapterNumber: chapter.id, // using id as chapterNumber based on existing logic
    title: chapter.title,
    finalText: chapter.content,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  const path = await saveChapterContentToStorage(uid, projectId, storageData);
  return path;
};

export const saveCheckpointToStorage = async (
  uid: string,
  projectId: string,
  chapterNumber: number,
  draftText: string
): Promise<string> => {
  const startTime = Date.now();
  const path = `users/${uid}/projects/${projectId}/checkpoints/chapter-${chapterNumber}-publication-editor.json`;
  const storageRef = ref(storage, path);
  const data = { chapterNumber, draftText, updatedAt: new Date().toISOString() };
  
  await uploadString(storageRef, JSON.stringify(data), 'raw', {
    contentType: 'application/json'
  });
  
  console.log(`[STORAGE AUDIT] Checkpoint Saved | Duration: ${Date.now() - startTime}ms | Path: ${path}`);
  return path;
};

export const loadCheckpointFromStorage = async (
  storagePath: string
): Promise<string | null> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");
    const token = await user.getIdToken();
    const proxyUrl = resolveProxyStorageUrl(storagePath);
    const response = await fetch(proxyUrl, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Proxy load error: ${response.status} ${errText}`);
    }

    const data = await response.json();
    return data.draftText || null;
  } catch (error) {
    console.error("Error loading checkpoint from storage:", error);
    return null;
  }
};

export const deleteCheckpointFromStorage = async (
  storagePath: string
): Promise<void> => {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    console.log(`[STORAGE AUDIT] Checkpoint Deleted | Path: ${storagePath}`);
  } catch (error) {
    console.warn("Checkpoint deletion warning (may already be deleted or missing):", error);
  }
};

export const saveRecoveryCheckpointToStorage = async <T extends Record<string, unknown>>(
  storagePath: string,
  payload: T,
): Promise<string> => {
  const startTime = Date.now();
  const storageRef = ref(storage, storagePath);
  await uploadString(storageRef, JSON.stringify(payload), 'raw', {
    contentType: 'application/json',
  });
  console.log(`[STORAGE AUDIT] Recovery Checkpoint Saved | Duration: ${Date.now() - startTime}ms | Path: ${storagePath}`);
  return storagePath;
};

export const loadRecoveryCheckpointFromStorage = async <T>(
  storagePath: string,
): Promise<T | null> => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    const proxyUrl = resolveProxyStorageUrl(storagePath);
    const response = await fetch(proxyUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Proxy load error: ${response.status} ${errText}`);
    }
    return await response.json() as T;
  } catch (error) {
    console.error('Error loading recovery checkpoint from storage:', error);
    return null;
  }
};

export const resolveChapterStoragePath = (chapter: any): string | null => {
  if (!chapter) return null;
  const path = chapter.storagePath ||
               chapter.storageJsonPath ||
               chapter.storageRef ||
               chapter.contentPath ||
               chapter.artifactPath ||
               chapter.finalTextStoragePath ||
               chapter.filePath;
  if (typeof path === 'string' && path.trim()) {
    return path.trim();
  }
  return null;
};
